import React, { useState } from 'react';
import Layout from '../components/Layout/Layout';
import { itineraryAPI } from '../services/api';
import './ItineraryTesting.css';

const INTERESTS = [
  { label: 'Culture Hunter',   value: 'Culture Hunter',   category: 'Attraction',              description: 'Historical & cultural attractions' },
  { label: 'Thrill Seeker',    value: 'Thrill Seeker',    category: 'Adventure, Entertainment', description: 'Adventure & entertainment' },
  { label: 'Natural Wonder',   value: 'Natural Wonder',   category: 'Natural Wonder',           description: 'Nature & eco-tourism' },
  { label: 'Foodie Trail',     value: 'Foodie Trail',     category: 'Restaurant',               description: 'Food & dining experiences' },
  { label: 'Retail Adventure', value: 'Retail Adventure', category: 'Shopping',                 description: 'Shopping & retail' },
];
const DISTRICTS_OPTIONS = ['Melaka Tengah', 'Ayer Keroh', 'Jasin'];
const FOOD_PREFS = ['any', 'halal', 'vegetarian', 'vegan'];

const TYPE_ICONS  = { restaurant: '🍽️', interest: '🎯', wildcard: '🎲' };
const STATUS_COLORS = { ok: 'ok', error: 'error', skipped: 'skipped', warning: 'warning' };

const STEP_LABELS = [
  'Input Received',
  'Interest Mapping',
  'RAG Candidate Search',
  'Slot Assignment',
  'Activity Selection',
  'Plan Assembled',
  'Save Decision',
];

const DebugStep = ({ index, step }) => {
  const [open, setOpen] = useState(false);
  const status = step?.status || 'ok';
  const cls = STATUS_COLORS[status] || 'ok';
  return (
    <div className={`it-debug-step ${cls}`}>
      <button className="it-debug-header" onClick={() => setOpen(o => !o)}>
        <span className="it-step-num">Step {index + 1}</span>
        <span className="it-step-name">{STEP_LABELS[index] || step?.step || `Step ${index + 1}`}</span>
        <span className={`it-badge ${cls}`}>{status}</span>
        {step?.duration_ms != null && <span className="it-ms">{step.duration_ms}ms</span>}
        <span className="it-chevron">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <pre className="it-debug-json">{JSON.stringify(step?.data ?? step, null, 2)}</pre>
      )}
    </div>
  );
};

const ItineraryTesting = () => {
  const [form, setForm] = useState({
    interests: [],
    districts: [],
    from_date: '',
    to_date: '',
    guests: 2,
    food_preferences: 'any',
    hotel_rating: 3,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [activeDay, setActiveDay] = useState(0);

  const toggleMulti = (field, val) => {
    setForm(f => {
      const arr = f[field];
      if (arr.includes(val)) return { ...f, [field]: arr.filter(v => v !== val) };
      const maxLen = field === 'interests' ? 2 : 10;
      if (arr.length >= maxLen) return f;
      return { ...f, [field]: [...arr, val] };
    });
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (form.interests.length === 0) { setError('Select at least one interest.'); return; }
    try {
      setLoading(true); setError(''); setResult(null);
      const payload = {
        user_id: 1,
        ...form,
        // dates from <input type="date"> are already YYYY-MM-DD
        food_preferences: !form.food_preferences || form.food_preferences === 'any'
          ? []
          : [form.food_preferences],
      };
      const data = await itineraryAPI.testGenerate(payload);
      setResult(data);
      setActiveDay(0);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Generation failed.');
    } finally {
      setLoading(false);
    }
  };

  const plan         = result?.data?.plan || {};
  const days         = plan?.days || [];
  const debugTrace   = result?.debug_trace?.steps || result?.pipeline_steps || [];
  const flags        = result?.map_data?.debug_flags || result?.debug_flags || [];
  const genMs        = result?.data?.metadata?.generation_time_ms || result?.generation_ms;
  const currentDay   = days[activeDay] || {};
  // API returns segments array: [{ name: 'Morning', activities: [...] }, ...]
  const segmentMap   = {};
  (currentDay?.segments || []).forEach(seg => {
    segmentMap[seg.name.toLowerCase()] = seg.activities || [];
  });

  return (
    <Layout title="Testing Console">
      <div className="it-testing">
        <div className="it-page-header">
          <h2>🧪 Testing Console</h2>
          {genMs && <span className="it-gen-time">⏱️ {(genMs / 1000).toFixed(2)}s</span>}
        </div>

        <div className="it-split">
          {/* LEFT — Input form */}
          <div className="it-left">
            <div className="it-card">
              <h3 className="it-card-title">Generate Itinerary</h3>
              <form onSubmit={handleGenerate} className="it-form">
                {/* Interests */}
                <div className="it-field">
                  <label>Interests <span className="it-hint">(max 2)</span></label>
                  <div className="it-chip-group">
                    {INTERESTS.map(({ label, value, category }) => (
                      <button
                        key={value} type="button"
                        className={`it-chip ${form.interests.includes(value) ? 'selected' : ''}`}
                        onClick={() => toggleMulti('interests', value)}
                      >
                        <span className="it-chip-label">{label}</span>
                        <span className="it-chip-cat">→ {category}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Districts */}
                <div className="it-field">
                  <label>Districts</label>
                  <div className="it-chip-group">
                    {DISTRICTS_OPTIONS.map(d => (
                      <button
                        key={d} type="button"
                        className={`it-chip ${form.districts.includes(d) ? 'selected' : ''}`}
                        onClick={() => toggleMulti('districts', d)}
                      >{d}</button>
                    ))}
                  </div>
                </div>

                {/* Dates */}
                <div className="it-row">
                  <div className="it-field">
                    <label>From Date</label>
                    <input type="date" className="it-input" value={form.from_date}
                      onChange={e => setForm(f => ({ ...f, from_date: e.target.value }))} />
                  </div>
                  <div className="it-field">
                    <label>To Date</label>
                    <input type="date" className="it-input" value={form.to_date}
                      onChange={e => setForm(f => ({ ...f, to_date: e.target.value }))} />
                  </div>
                </div>

                {/* Guests + Hotel */}
                <div className="it-row">
                  <div className="it-field">
                    <label>Guests</label>
                    <input type="number" min={1} max={20} className="it-input" value={form.guests}
                      onChange={e => setForm(f => ({ ...f, guests: Number(e.target.value) }))} />
                  </div>
                  <div className="it-field">
                    <label>Hotel Rating</label>
                    <select className="it-select" value={form.hotel_rating}
                      onChange={e => setForm(f => ({ ...f, hotel_rating: Number(e.target.value) }))}>
                      {[1,2,3,4,5].map(r => <option key={r} value={r}>{r}★</option>)}
                    </select>
                  </div>
                  <div className="it-field">
                    <label>Food Preference</label>
                    <select className="it-select" value={form.food_preferences}
                      onChange={e => setForm(f => ({ ...f, food_preferences: e.target.value }))}>
                      {FOOD_PREFS.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>

                {error && <div className="it-alert error">{error}</div>}

                <button type="submit" className="it-generate-btn" disabled={loading}>
                  {loading ? '⏳ Generating…' : '🚀 Generate Itinerary'}
                </button>
              </form>
            </div>

            {/* Result map placeholder + itinerary */}
            {result && days.length > 0 && (
              <div className="it-card">
                <div className="it-day-tabs">
                  {days.map((_, i) => (
                    <button key={i} className={`it-day-tab ${activeDay === i ? 'active' : ''}`}
                      onClick={() => setActiveDay(i)}>Day {i + 1}</button>
                  ))}
                </div>

                {/* Map placeholder — swap with Google Maps embed when API key available */}
                <div className="it-map-placeholder">
                  <div className="it-map-inner">
                    <div className="it-map-label">🗺️ Route Map</div>
                    <div className="it-map-hint">Integrate Google Maps API key to display route</div>
                    <div className="it-map-pins">
                      {['morning', 'afternoon', 'evening'].flatMap(slot =>
                        (segmentMap[slot] || []).map((act, i) => (
                          <div key={`${slot}-${i}`} className={`it-pin ${act?.type || 'interest'}`}>
                            {i + 1}. {act?.name || act?.attraction_name || '—'}
                          </div>
                        ))
                      )}
                    </div>
                    <div className="it-map-legend">
                      <span className="it-legend restaurant">🍽️ Restaurant</span>
                      <span className="it-legend interest">🎯 Interest</span>
                      <span className="it-legend wildcard">🎲 Wildcard</span>
                    </div>
                  </div>
                </div>

                {/* Activities list */}
                {['morning', 'afternoon', 'evening'].map(slot => (
                  <div key={slot} className="it-slot-section">
                    <div className="it-slot-label">
                      {slot === 'morning' ? '🌅' : slot === 'afternoon' ? '☀️' : '🌙'} {slot}
                    </div>
                    {(segmentMap[slot] || []).map((act, i) => {
                      const type = act?.type || act?.activity_type || 'interest';
                      const hasFlag = flags.some(f => f.attraction_id === act?.id);
                      return (
                        <div key={i} className={`it-activity-card ${hasFlag ? 'flagged' : ''}`}>
                          <span className={`it-type-pill ${type}`}>
                            {TYPE_ICONS[type] || '📍'} {type}
                          </span>
                          <div className="it-act-name">{act?.name || act?.attraction_name || '—'}</div>
                          {act?.category && <div className="it-act-cat">{act.category}</div>}
                          {hasFlag && <div className="it-flag-warn">⚠️ Debug flag</div>}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT — Debug panel */}
          <div className="it-right">
            {result ? (
              <div className="it-card">
                <h3 className="it-card-title">Debug Pipeline</h3>

                {flags.length > 0 && (
                  <div className="it-flags-section">
                    <div className="it-flags-title">⚠️ Debug Flags ({flags.length})</div>
                    {flags.map((f, i) => (
                      <div key={i} className="it-flag-item">{f.message || JSON.stringify(f)}</div>
                    ))}
                  </div>
                )}

                <div className="it-debug-steps">
                  {debugTrace.length === 0 ? (
                    <div className="it-empty">No debug trace in response.</div>
                  ) : debugTrace.map((step, i) => (
                    <DebugStep key={i} index={i} step={step} />
                  ))}
                </div>

                {/* Raw JSON */}
                <details className="it-raw-details">
                  <summary>Raw Response JSON</summary>
                  <pre className="it-debug-json">{JSON.stringify(result, null, 2)}</pre>
                </details>
              </div>
            ) : (
              <div className="it-card it-empty-state">
                <div className="it-empty-icon">🧪</div>
                <div className="it-empty-text">Fill in the form and click Generate to see the debug pipeline here.</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ItineraryTesting;
